import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PropertyContext = createContext(null);

const LS_KEY = "lodgy.activePropertyId";
const ALL = "all";

/**
 * Global "active property" state (Rentlio-style property switcher).
 *
 * - `activePropertyId` is `null` when the user wants to see ALL properties
 *   (the aggregated cross-property view), or a UUID when a single property
 *   is selected.
 * - The choice is persisted to localStorage so the switcher survives reloads.
 * - When the user's tenant only owns a single property, we auto-select it and
 *   hide the switcher UI (see `PropertySwitcher`).
 */
export function PropertyProvider({ children }) {
  const { user, tenant } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePropertyId, setActivePropertyIdState] = useState(() => {
    const stored = localStorage.getItem(LS_KEY);
    if (!stored || stored === ALL) return null;
    return stored;
  });

  const refresh = useCallback(async () => {
    if (!user || !tenant) return;
    setLoading(true);
    try {
      const { data } = await api.get("/properties");
      setProperties(Array.isArray(data) ? data : []);
    } catch {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [user, tenant]);

  useEffect(() => {
    if (user && tenant) refresh();
    if (!user) setProperties([]);
  }, [user, tenant, refresh]);

  // If the persisted property no longer exists (deleted, tenant switched…),
  // fall back to "all".
  useEffect(() => {
    if (!properties.length || !activePropertyId) return;
    if (!properties.some((p) => p.id === activePropertyId)) {
      setActivePropertyIdState(null);
      localStorage.setItem(LS_KEY, ALL);
    }
  }, [properties, activePropertyId]);

  const setActivePropertyId = useCallback((id) => {
    const next = id && id !== ALL ? id : null;
    setActivePropertyIdState(next);
    localStorage.setItem(LS_KEY, next || ALL);
  }, []);

  const activeProperty = useMemo(
    () => (activePropertyId ? properties.find((p) => p.id === activePropertyId) || null : null),
    [activePropertyId, properties]
  );

  const value = useMemo(
    () => ({
      properties,
      loading,
      activePropertyId,      // null = all
      activeProperty,        // full object or null
      setActivePropertyId,
      refresh,
    }),
    [properties, loading, activePropertyId, activeProperty, setActivePropertyId, refresh]
  );

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export function useProperty() {
  const ctx = useContext(PropertyContext);
  if (!ctx) throw new Error("useProperty must be used inside PropertyProvider");
  return ctx;
}
